import { joinSupplierPartRows } from "./joinSupplierPartRows";
import {
  PartConfirmationBucketEntry,
  PartQuantity,
  SupplierPart,
} from "./partsCalculationUtils";

/**
 * Build a part-confirmation send payload for one supplier.
 *
 * Uses the exact same `joinSupplierPartRows` + `Math.min(askedQty, stockQty)` logic as
 * `RequestPartConfirmationModal`, so batch "Send Part Confirmation" from the estimate
 * bucket produces an identical payload to the single-supplier request.
 *
 * `askedParts` should be the quantities to request (typically the supplier's selected
 * parts in the estimate bucket).
 *
 * Returns `null` when the supplier has no matching in-stock rows.
 */
export const buildPartConfirmationBucketEntry = (
  supplierLabel: string,
  remainingParts: PartQuantity[],
  supplierPartsForLabel: Array<SupplierPart & { id?: string }>,
): PartConfirmationBucketEntry | null => {
  const [supplierName, supplierId] = supplierLabel.split("::");
  if (!supplierId) {
    return null;
  }

  const rows = joinSupplierPartRows(
    remainingParts,
    supplierPartsForLabel ?? [],
  );
  if (rows.length === 0) {
    return null;
  }

  return {
    supplierId,
    supplierName: supplierName || supplierLabel,
    addedAt: new Date(),
    confirmationParts: rows.map((row) => ({
      partId: row.partId,
      partNumber: row.partNumber,
      rootPartNumber: row.rootPartNumber,
      partName: row.partName,
      // Ask for at most what's actually available right now (effective).
      askedQty: Math.min(row.askedQty, row.stockQty),
      // Stamp effective availability: TS − consumption − confirmed − pending.
      stockQty: row.stockQty,
      binLocation: row.binLocation,
      parts: row.parts,
      alternateParts: row.alternateParts,
    })),
    selectedParts: rows.map((row) => ({
      rootPartNumber: row.rootPartNumber,
      familyQuantity: Math.min(row.askedQty, row.stockQty),
    })),
  };
};
